import * as React from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { trackError } from '../../../../utils';

/*
  based on the okta login callback component here: https://github.com/okta/okta-react/blob/master/src/LoginCallback.tsx we remove bits we dont use 
  and we took out the handle redirect code from a useEffect that could potentially be called multiple times 
*/

interface LoginCallbackProps {
  errorComponent: React.ComponentType<{ error: Error }>;
}

const clearOktaStorageValues = () => {
  //local storage
  const localStoragekeys = Object.keys(window.localStorage).filter((key) => key.includes('okta'));
  localStoragekeys.forEach((key) => window.localStorage.removeItem(key));

  //session storage
  const sessionStoragekeys = Object.keys(window.sessionStorage).filter((key) =>
    key.includes('okta'),
  );
  sessionStoragekeys.forEach((key) => window.sessionStorage.removeItem(key));
};

let handledRedirect = false;

export const CustomLoginCallback: React.FC<LoginCallbackProps> = ({ errorComponent }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const [callbackError, setCallbackError] = React.useState(null);

  const ErrorReporter = errorComponent;
  if (oktaAuth && !handledRedirect) {
    oktaAuth.handleLoginRedirect().catch((e) => {
      // eslint-disable-next-line no-console
      console.log(e);
      trackError('login.callback.error', 'login.callback.error', e);
      if (
        /**
         *  in an attempt to prevent the user being blocked by the PKCE error here we implemented the solution in this comment:
         *  https://github.com/okta/okta-oidc-js/issues/898#issuecomment-954852346
         *  we basically clear out any okta related storage values and sent the user back to the homepage to try to log them in again
         *  if the cookie is already set it should detect it and prevent trying to log in again
         */
        e.errorSummary &&
        e.errorSummary.includes('Could not load PKCE codeVerifier from storage')
      ) {
        trackError('login.callback.error', 'login.callback.PKCE.error', e);
        clearOktaStorageValues();
        window.location.replace('/');
      }
      setCallbackError(e);
    });
    handledRedirect = true;
  }

  const authError = authState?.error;
  const displayError = callbackError || authError;
  if (displayError) {
    return <ErrorReporter error={displayError} />;
  }

  return null;
};
